import React, { useState, useEffect } from "react";
import axios from "axios";
import { RiEditLine, RiDeleteBinLine, RiCloseLine } from "react-icons/ri";
import Swal from "sweetalert2";
import { confirmAndDelete } from "../../utils/confirmAndDelete";
const BookingSkeleton = () => (
  <div className="animate-pulse bg-white rounded-2xl shadow-lg border border-gray-200 p-5 space-y-4">
    <div className="h-4 w-24 bg-gray-200 rounded"></div>
    <div className="flex items-center">
      <div className="w-16 h-16 bg-gray-200 rounded-lg mr-4"></div>
      <div>
        <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
        <div className="h-3 w-20 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="h-3 w-full bg-gray-200 rounded"></div>
    <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
    <div className="flex justify-end space-x-2">
      <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
      <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);
const NoBookingsFound = () => {
  return (
    <div className="flex w-full mx-auto flex-col items-center justify-center py-20 text-center">
      <img
        src="https://cdn-icons-png.flaticon.com/512/4076/4076549.png"
        alt="No Data"
        className="w-24 h-24 mb-6 opacity-70"
      />
      <h2 className="text-xl font-bold text-primaryTextColor mb-2">
        No Bookings Found
      </h2>
      <p className="text-gray-500">There are no bookings at the moment.</p>
    </div>
  );
};

const EventsBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // <-- NEW state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statuses = ["pending", "confirmed", "completed", "cancelled"];

  // Fetch bookings from API
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/all-events`
      );
      setBookings(res.data);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Delete booking
  const deleteBooking = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/events/delete-event`,
    });

    if (isConfirmed) {
      try {
        setBookings(bookings.filter((b) => b._id !== id));
        Swal.fire({
          title: "Deleted!",
          text: "Booking has been removed successfully.",
          icon: "success",
          confirmButtonColor: "#10B981",
          background: "#fff",
        });
      } catch (err) {
        console.error("Error deleting booking:", err);
      }
    }
  };

  // Open edit modal
  const openEditModal = (booking) => {
    setEditingBooking({ ...booking });
    setIsModalOpen(true);
  };

  // Handle status change in modal
  const handleStatusChange = (e) => {
    setEditingBooking({ ...editingBooking, status: e.target.value });
  };

  // Submit status change
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.put(
        `${process.env.REACT_APP_API_URL}/events/change-events-status/${editingBooking._id}`,
        { status: editingBooking.status }
      );
      setBookings(
        bookings.map((b) => (b._id === editingBooking._id ? editingBooking : b))
      );
      setIsModalOpen(false);
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent p-6 tracking-wide">
      {/* <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnHoverColor to-primaryBtnColor bg-clip-text text-transparent">
        All Field Bookings
      </h1> */}

      <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
        All Field Bookings
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {loading ? (
          Array.from({ length: 4 }).map((_, idx) => (
            <BookingSkeleton key={idx} />
          ))
        ) : bookings?.length === 0 ? (
          <NoBookingsFound />
        ) : (
          bookings.map((booking) => (
            <div
              key={booking._id}
              className="relative bg-white rounded-2xl shadow-lg border border-primaryTextColor p-5"
            >
              {/* Event Type Badge */}
              <span
                className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold ${
                  booking.eventType === "single"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {booking.eventType.charAt(0).toUpperCase() +
                  booking.eventType.slice(1)}
              </span>
              {/* Field */}
              <div className="flex items-center mb-4">
                <img
                  src={booking?.field?.imageUrl}
                  alt={booking?.field?.fieldName}
                  className="w-16 h-16 rounded-lg object-cover mr-4"
                />
                <div>
                  <h2 className="text-lg font-bold text-primaryTextColor font-heading">
                    {booking?.field?.fieldName}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {booking?.field?.price} {booking?.field?.time}
                  </p>
                </div>
              </div>

              {/* Event Type */}
              {booking.eventType === "single" ? (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-semibold">Teams:</span>{" "}
                    {booking.teamOne} vs {booking.teamTwo}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Match Date:</span>{" "}
                    {new Date(booking.matchDate).toLocaleDateString()}
                  </p>
                </div>
              ) : (
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-semibold">Tournament:</span>{" "}
                    {booking.numberOfTeams} Teams, {booking.numberOfMatches}{" "}
                    Matches
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">Date Range:</span>{" "}
                    {new Date(booking.dateRange.start).toLocaleDateString()} -{" "}
                    {new Date(booking.dateRange.end).toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* Booked By */}
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Booked By:</span>{" "}
                {booking.bookedBy.name} ({booking.bookedBy.role})
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">Contact:</span>{" "}
                {booking.bookedBy.email}, {booking.bookedBy.phone}
              </p>

              {/* Notes */}
              <p className="text-sm text-gray-500 italic mb-3">
                {booking.notes}
              </p>

              {/* Status */}
              <p className="text-sm mb-3">
                <span className="font-semibold">Status:</span>{" "}
                <span
                  className={`px-2 py-1 rounded-lg text-xs ${
                    booking.status === "pending"
                      ? "bg-yellow-100 text-yellow-700"
                      : booking.status === "approved"
                      ? "bg-green-100 text-green-700"
                      : booking.status === "completed"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {booking.status}
                </span>
              </p>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => openEditModal(booking)}
                  className="p-2 bg-gray-100 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white"
                >
                  <RiEditLine />
                </button>
                <button
                  onClick={() => deleteBooking(booking._id)}
                  className="p-2 bg-gray-100 rounded-lg text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <RiDeleteBinLine />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {isModalOpen && editingBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg border border-primaryTextColor shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-primaryTextColor">
                Change Status
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-primaryBtnHoverColor"
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm mb-2">Status</label>
                <select
                  value={editingBooking.status}
                  onChange={handleStatusChange}
                  className="w-full border border-gray-300 rounded-lg p-3"
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-200 text-secondaryTextColor py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primaryBtnColor text-white w-40 flex items-center justify-center py-2 px-4 rounded-lg hover:bg-primaryBtnHoverColor"
                >
                  {isSubmitting ? (
                    <div class="spinner w-full"></div>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsBookings;
